<template>
  <div class="clearfix">
    <!-- 文件上传组件 -->
    <a-upload
      :disabled="disabled"
      :file-list="fileList"
      :list-type="type === 'image' ? 'picture-card' : 'picture'"
      :show-upload-list="true"
      :accept="accept"
      :before-upload="beforeUpload"
      :customRequest="handleChange"
      @change="changeImg"
      @preview="handlePreview"
    >
      <!-- 文件和视频类型显示上传按钮 -->
      <div v-if="type === 'file' || type === 'video'">
        <a-button>
          <a-icon type="upload" />
<!--          上传-->
        </a-button>
      </div>
      <!-- 图片类型显示上传图标（未达到数量限制时） -->
      <div v-if="!disabled && fileList.length < count">
        <span v-if="type === 'image'">
          <a-icon :type="loading ? 'loading' : 'plus'" />
<!--          <div class="ant-upload-text">上传</div>-->
        </span>
      </div>
    </a-upload>

    <!-- 文件类型时显示链接 -->
    <span v-if="value && type === 'file'">
      <br />
      <a :href="value" target="_blank">{{ value }}</a>
    </span>

    <!-- 图片预览弹窗 -->
    <a-modal :visible="previewVisible" :footer="null" @cancel="handleCancel">
      <img alt="preview" style="width: 100%" :src="previewImage" />
    </a-modal>
  </div>
</template>

<script>
/**
 * 文件上传组件
 * 支持图片、文件、视频上传到腾讯云 COS
 * 支持单文件和多文件上传
 */
import UploadFileToOSS from '@/utils/upload-file-to-oss.js'

export default {
  name: 'FileUpload',
  props: {
    /** 是否禁用 */
    disabled: {
      type: Boolean,
      default: false,
    },
    /** 文件URL（v-model 绑定值） */
    value: {
      type: String,
      default: '',
    },
    /** 文件类型：image-图片，file-文件，video-视频，mp3-音频 */
    type: {
      type: String,
      default: 'image',
      validator: (value) => ['image', 'file', 'video', 'mp3'].includes(value),
    },
    /** 允许上传的文件数量 */
    count: {
      type: Number,
      default: 1,
    },
    /** 默认文件列表 */
    defaultList: {
      type: Array,
      default() {
        return []
      },
    },
  },
  watch: {
    /**
     * 监听 value 变化，同步更新 fileList
     * 当外部修改 value 时，自动更新内部文件列表
     */
    value: {
      handler(newVal) {
        if (!newVal) {
          // value 为空时，清空 fileList（仅单文件模式）
          if (this.count === 1) {
            this.fileList = []
          }
          return
        }

        // 标准化 URL 进行比较
        const normalizeUrl = (url) => {
          if (!url) return ''
          return String(url).trim().replace(/\/+$/, '')
        }
        const normalizedNewVal = normalizeUrl(newVal)

        // 检查 fileList 中是否已存在相同的 URL
        const existingItem = this.fileList.find(item => {
          const itemUrl = item.url || item.path
          return normalizeUrl(itemUrl) === normalizedNewVal
        })

        // 如果不存在且为单文件模式，则更新 fileList
        if (!existingItem && this.count === 1) {
          this.fileList = [{
            uid: `-${Date.now()}`,
            status: 'done',
            url: newVal,
            path: newVal,
            name: newVal.split('/').pop() || 'file',
            cover: ''
          }]
        }
      },
    }
  },
  data() {
    return {
      loading: false, // 上传加载状态
      previewVisible: false, // 预览弹窗显示状态
      previewImage: '', // 预览图片URL
      fileList: [], // 文件列表
      accept: '', // 接受的文件类型
    }
  },
  created() {
    // 根据文件类型设置 accept 属性
    const acceptMap = {
      image: 'image/*',
      mp3: 'audio/mp3',
      video: 'video/*',
      file: ''
    }
    this.accept = acceptMap[this.type] || ''

    // 初始化文件列表
    if (this.value) {
      this.fileList = [{
        uid: `-${Date.now()}`,
        status: 'done',
        url: this.value,
        path: this.value,
        name: this.value.split('/').pop() || 'file',
        cover: ''
      }]
    } else {
      this.fileList = [...this.defaultList]
    }
  },
  methods: {
    /**
     * 上传前的文件校验
     * @param {File} file - 待上传的文件
     * @returns {Boolean} - 是否允许上传
     */
    beforeUpload(file) {
      const fileSize = file.size / 1024 / 1024 // 文件大小（MB）

      // 视频文件大小限制
      if (file.type === 'video/mp4') {
        if (fileSize >= 20) {
          this.$message.error('视频大小限制 20MB!')
          return false
        }
        return true
      }

      // 音频文件大小限制
      if (file.type === 'audio/mp3') {
        if (fileSize >= 10) {
          this.$message.error('音频大小限制 10MB!')
          return false
        }
        return true
      }

      // 图片文件大小限制（支持所有图片类型）
      if (file.type.startsWith('image/')) {
        if (fileSize >= 2) {
          this.$message.error('图片大小限制 2MB!')
          return false
        }
        return true
      }

      return true
    },
    /**
     * 处理文件上传
     * @param {Object} info - 上传信息对象，包含 file 属性
     */
    handleChange(info) {
      this.loading = true

      // 使用腾讯云 COS 上传
      new UploadFileToOSS([info.file], {
        success: (res) => {
          // 单个文件上传成功的回调
          if (res && res.length > 0) {
            // 标准化 URL 用于比较（去除首尾空格和末尾斜杠）
            const normalizeUrl = (url) => {
              if (!url) return ''
              return String(url).trim().replace(/\/+$/, '')
            }

            // 获取已有文件的 URL 列表
            const existingUrls = this.fileList.map(item => {
              const url = item.url || item.path
              return normalizeUrl(url)
            }).filter(Boolean)

            // 过滤掉已存在的文件
            const newFiles = res.filter(item => {
              const url = item.url || item.path
              const normalizedUrl = normalizeUrl(url)
              return normalizedUrl && !existingUrls.includes(normalizedUrl)
            })

            // 更新文件列表
            if (newFiles.length > 0) {
              if (this.count === 1) {
                // 单文件模式：替换原有文件
                this.fileList = newFiles
              } else {
                // 多文件模式：追加新文件
                this.fileList = this.fileList.concat(newFiles)
              }

              // 通知父组件
              this.notifyParent(newFiles)
            }
          }
        },
        finally: () => {
          // 上传完成（成功或失败）
          this.loading = false
          this.$nextTick(() => {
            this.$forceUpdate()
          })
        },
        error: () => {
          this.loading = false
          this.$message.error('上传失败，请重试')
        }
      })
    },
    /**
     * 通知父组件文件变化
     * @param {Array} res - 新上传的文件列表
     */
    notifyParent(res) {
      if (!res || res.length === 0) return

      if (this.count === 1) {
        // 单文件模式：触发 v-model 和 change 事件
        const url = res[0].url || res[0].path
        if (url) {
          this.$emit('input', url) // v-model 支持
          this.$emit('change', url) // change 事件
        }
      } else {
        // 多文件模式：传递所有文件的 URL 列表
        const allUrls = this.fileList.map(item => item.url || item.path).filter(Boolean)
        this.$emit('change', allUrls)
      }
    },
    /**
     * 文件列表变化处理
     * @param {Object} info - 文件信息对象
     */
    changeImg(info) {
      // 处理文件删除
      if (info.file.status === 'removed') {
        if (this.count === 1) {
          // 单文件模式：清空列表并通知父组件
          this.fileList = []
          this.$emit('input', '')
          this.$emit('change', '')
        } else {
          // 多文件模式：从列表中移除指定文件
          const index = this.fileList.findIndex(item => item.uid === info.file.uid)
          if (index > -1) {
            this.fileList.splice(index, 1)
            // 通知父组件更新
            const allUrls = this.fileList.map(item => item.url || item.path).filter(Boolean)
            this.$emit('change', allUrls)
          }
        }
        this.$forceUpdate()
      }
    },
    /**
     * 图片预览
     * @param {Object} file - 文件对象
     */
    handlePreview(file) {
      // 如果文件已有 URL 或预览图，直接使用
      if (file.url || file.preview) {
        this.previewImage = file.url || file.preview
        this.previewVisible = true
        return
      }

      // 如果文件有原始文件对象，使用 FileReader 生成预览
      if (file.originFileObj) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.previewImage = e.target.result
          this.previewVisible = true
        }
        reader.readAsDataURL(file.originFileObj)
      }
    },

    /**
     * 关闭预览弹窗
     */
    handleCancel() {
      this.previewVisible = false
    },
  },
}
</script>
<style lang="less" scoped>
/* 图片样式 */
img {
  width: 128px;
  height: 128px;
}

/* 上传按钮图标样式 */
.ant-upload-select-picture-card i {
  font-size: 32px;
  color: #999;
}

/* 上传按钮文字样式 */
.ant-upload-select-picture-card .ant-upload-text {
  margin-top: 8px;
  color: #666;
}
</style>
